import { useEffect, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, ListCard, Row, SegmentedControl } from '@gonka-wallet/ui-tokens';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import {
  openWalletDb,
  saveSettings,
  wipeAll,
  deleteWallet,
  deleteWatchedAddress,
  LOCALES,
  type Locale,
  type ChainNodeSource,
  type Settings as SettingsModel,
} from '@gonka-wallet/storage';
import { shortenAddress } from '@gonka-wallet/crypto';
import { useThemeStore, type ThemePreference } from '../stores/themeStore.js';
import { useSettingsStore } from '../stores/settingsStore.js';
import { useWalletStore } from '../stores/walletStore.js';
import { useWatchedModalStore } from '../stores/watchedModalStore.js';
import { useSessionStore } from '../stores/sessionStore.js';
import { changeLocale } from '../i18n/index.js';
import { InfoTooltip } from '../components/InfoTooltip.js';
import { PRESET_NODES, DEFAULT_NODE_URLS } from '../lib/chainNodes.js';
import {
  WalletIcon,
  GlobeIcon,
  ShieldIcon,
  EyeIcon,
  InfoIcon,
  TrashIcon,
  SunIcon,
} from '../components/settingsIcons.js';

const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  ru: 'Русский',
  zh: '简体中文',
  system: 'System',
};

const AUTO_LOCK_CHOICES: Array<SettingsModel['autoLockMinutes']> = [
  1,
  5,
  15,
  30,
  0,
];

export function Settings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = useSettingsStore((s) => s.locale);
  const chainNodeSource = useSettingsStore((s) => s.chainNodeSource);
  const customNodeUrl = useSettingsStore((s) => s.customNodeUrl);
  const presetNodeId = useSettingsStore((s) => s.presetNodeId);
  const setPresetNodeId = useSettingsStore((s) => s.setPresetNodeId);
  const autoLockMinutes = useSettingsStore((s) => s.autoLockMinutes);
  const setAll = useSettingsStore((s) => s.setAll);
  const wallets = useWalletStore((s) => s.wallets);
  const watched = useWalletStore((s) => s.watched);
  const activeId = useWalletStore((s) => s.activeId);
  const setWallets = useWalletStore((s) => s.setWallets);
  const setActive = useWalletStore((s) => s.setActive);
  const removeWalletFromStore = useWalletStore((s) => s.removeWallet);
  const removeWatchedFromStore = useWalletStore((s) => s.removeWatched);
  const openWatchedModal = useWatchedModalStore((s) => s.open);
  const sessionReset = useSessionStore((s) => s.reset);
  const sessionLock = useSessionStore((s) => s.lock);
  const themePreference = useThemeStore((s) => s.preference);
  const setThemePreference = useThemeStore((s) => s.setPreference);
  const [wipeConfirm, setWipeConfirm] = useState('');
  const [customNodeDraft, setCustomNodeDraft] = useState(customNodeUrl ?? '');
  const [removeTarget, setRemoveTarget] = useState<
    | { readonly type: 'wallet'; readonly id: string; readonly name: string }
    | { readonly type: 'watched'; readonly id: string; readonly name: string }
    | null
  >(null);

  async function persist(patch: Partial<SettingsModel>) {
    const db = await openWalletDb();
    const merged = await saveSettings(db, patch);
    setAll(merged);
    return merged;
  }

  async function onLocaleChange(next: Locale) {
    await persist({ locale: next });
    if (next !== 'system') {
      await changeLocale(next);
    }
  }

  async function onChainNodeSourceChange(next: ChainNodeSource) {
    // Auto-select first preset on initial switch to 'preset' so the chain
    // queries actually re-route — otherwise presetNodeId stays null and
    // resolveSeeds silently falls back to DEFAULT_RPC_SEEDS, making the
    // segment switch feel like a no-op from the user's perspective.
    if (next === 'preset' && presetNodeId === null && PRESET_NODES.length > 0) {
      const firstId = PRESET_NODES[0]!.id;
      setPresetNodeId(firstId);
      await persist({ chainNodeSource: next, presetNodeId: firstId });
      return;
    }
    await persist({ chainNodeSource: next });
  }

  async function onPresetNodeChange(id: string) {
    await persist({ presetNodeId: id });
  }

  async function onAutoLockChange(next: SettingsModel['autoLockMinutes']) {
    await persist({ autoLockMinutes: next });
  }

  async function onCustomNodeBlur() {
    const trimmed = customNodeDraft.trim();
    if (trimmed.length === 0) {
      await persist({ customNodeUrl: null });
      return;
    }
    try {
      await persist({ customNodeUrl: trimmed });
    } catch {
      setCustomNodeDraft(customNodeUrl ?? '');
    }
  }

  async function wipe() {
    if (wipeConfirm !== 'WIPE') return;
    const db = await openWalletDb();
    await wipeAll(db);
    setWallets([]);
    sessionReset();
    navigate('/wiped');
  }

  function lock() {
    sessionLock();
    navigate('/unlock');
  }

  async function removeOne(id: string) {
    const db = await openWalletDb();
    await deleteWallet(db, id);
    removeWalletFromStore(id);
    setRemoveTarget(null);
    // If the user just removed the last wallet, send them to /welcome
    // so they can create or import again.
    if (wallets.length <= 1) {
      sessionReset();
      navigate('/welcome');
    }
  }

  async function confirmRemove() {
    if (!removeTarget) return;
    if (removeTarget.type === 'wallet') {
      await removeOne(removeTarget.id);
    } else {
      await removeWatched(removeTarget.id);
      setRemoveTarget(null);
    }
  }

  async function removeWatched(id: string): Promise<void> {
    const db = await openWalletDb();
    await deleteWatchedAddress(db, id);
    removeWatchedFromStore(id);
  }

  const commit =
    (globalThis as { __BUILD_COMMIT__?: string }).__BUILD_COMMIT__ ?? 'dev';

  return (
    <div className="flex min-h-screen flex-col bg-bg px-4 pb-20 pt-3 text-text-primary">
      <h1 className="mb-2 mt-1 text-[22px] font-semibold tracking-[-0.5px] text-text-primary">
        {t('settings.title')}
      </h1>

      {/* Wallets — separate, visually distinct */}
      <CollapsibleSection storageKey="wallets" icon={<WalletIcon />} label={t('settings.wallets')}>
        <ListCard>
          {wallets.map((w) => {
            const isActive = w.id === activeId;
            const addr = w.addresses[0];
            return (
              <div
                key={w.id}
                className="grid grid-cols-[1fr_auto] items-center gap-3 py-[11px]"
              >
                <button
                  onClick={() => setActive(w.id)}
                  className="min-w-0 flex-1 text-left"
                >
                  <div
                    className={[
                      'truncate text-sm font-medium',
                      isActive ? 'text-link' : 'text-text-primary',
                    ].join(' ')}
                  >
                    {w.name}
                  </div>
                  {addr && (
                    <div className="truncate font-mono text-xs text-text-tertiary">
                      {shortenAddress(addr)}
                    </div>
                  )}
                </button>
                <button
                  onClick={() =>
                    setRemoveTarget({ type: 'wallet', id: w.id, name: w.name })
                  }
                  className="rounded-lg border border-border-hairline px-3 py-1 text-xs text-text-secondary hover:text-neg"
                  aria-label={t('settings.removeWallet')}
                >
                  ×
                </button>
              </div>
            );
          })}
        </ListCard>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Button variant="secondary" onClick={() => navigate('/create')}>
            {t('settings.addWalletCreate')}
          </Button>
          <Button variant="secondary" onClick={() => navigate('/import')}>
            {t('settings.addWalletImport')}
          </Button>
        </div>
      </CollapsibleSection>

      {/* Watched Addresses — separate, visually distinct */}
      <CollapsibleSection
        storageKey="watched"
        icon={<EyeIcon />}
        label={t('watchedAddress.settingsSection')}
      >
        {watched.length === 0 ? (
          <div className="mb-2 text-xs text-text-tertiary">
            {t('watchedAddress.settingsEmpty')}
          </div>
        ) : (
          <ListCard className="mb-3">
            {watched.map((w) => (
              <div
                key={w.id}
                className="grid grid-cols-[1fr_auto] items-center gap-3 py-[11px]"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-text-primary">{w.name}</div>
                  <div className="truncate font-mono text-xs text-text-tertiary">
                    {shortenAddress(w.address)}
                  </div>
                </div>
                <button
                  onClick={() =>
                    setRemoveTarget({ type: 'watched', id: w.id, name: w.name })
                  }
                  className="rounded-lg border border-border-hairline px-3 py-1 text-xs text-text-secondary hover:text-neg"
                  aria-label={t('watchedAddress.remove')}
                >
                  ×
                </button>
              </div>
            ))}
          </ListCard>
        )}
        <Button
          variant="secondary"
          onClick={openWatchedModal}
          fullWidth
        >
          + {t('watchedAddress.add')}
        </Button>
      </CollapsibleSection>

      {/* Security */}
      <CollapsibleSection storageKey="security" icon={<ShieldIcon />} label={t('settings.section.security')}>
        <ListCard>
          <Row
            label={t('settings.changePin')}
            value={<span className="text-lg text-text-disabled">›</span>}
            onClick={() => navigate('/settings/change-pin')}
          />
          <Row
            label={t('settings.showMnemonic')}
            value={<span className="text-lg text-text-disabled">›</span>}
            onClick={() => navigate('/settings/show-mnemonic')}
          />
          <Row
            label={t('settings.biometric')}
            value={<span className="text-lg text-text-disabled">›</span>}
            onClick={() => navigate('/settings/biometric')}
          />
          <Row
            label={t('settings.lock')}
            meta={t('settings.lockHint')}
            value={<span className="text-lg text-text-disabled">›</span>}
            onClick={lock}
          />
        </ListCard>
        <div className="mt-3 flex flex-col gap-2 rounded-2xl bg-surface-1 p-3">
          <div className="text-[9px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
            {t('settings.autoLock')}
          </div>
          <SegmentedControl<string>
            options={AUTO_LOCK_CHOICES.map((m) => String(m))}
            value={String(autoLockMinutes)}
            onChange={(v) => void onAutoLockChange(Number(v) as SettingsModel['autoLockMinutes'])}
            renderLabel={(v) => {
              const m = Number(v);
              return m === 0 ? t('settings.off') : t('settings.minutes', { count: m });
            }}
            ariaLabel={t('settings.autoLock')}
          />
        </div>
      </CollapsibleSection>

      {/* Appearance */}
      <CollapsibleSection storageKey="appearance" icon={<SunIcon />} label={t('settings.section.appearance')}>
        <div className="flex flex-col gap-3 rounded-2xl bg-surface-1 p-3">
          <div>
            <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
              {t('settings.theme')}
            </div>
            <SegmentedControl<ThemePreference>
              options={['system', 'light', 'dark'] as const}
              value={themePreference}
              onChange={setThemePreference}
              renderLabel={(opt) => opt.charAt(0).toUpperCase() + opt.slice(1)}
              ariaLabel="Theme preference"
            />
          </div>
          <div>
            <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
              {t('settings.language')}
            </div>
            <SegmentedControl<Locale>
              options={LOCALES}
              value={locale}
              onChange={(v) => void onLocaleChange(v)}
              renderLabel={(v) => LOCALE_LABELS[v]}
              ariaLabel={t('settings.language')}
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* Network */}
      <CollapsibleSection storageKey="network" icon={<GlobeIcon />} label={t('settings.section.network')}>
        <div className="flex flex-col gap-3 rounded-2xl bg-surface-1 p-3">
          <div>
            <div className="mb-1.5 flex items-center gap-2 text-[9px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
              <span>{t('settings.chainNode')}</span>
              {chainNodeSource === 'gonka-hub' && (
                <InfoTooltip ariaLabel={t('settings.chainNodeGonkaHub')}>
                  <div className="font-normal normal-case tracking-normal">
                    <div className="mb-1.5">{t('settings.chainNodeDefaultExplanation')}</div>
                    <div className="font-mono text-[10px]">
                      <div>
                        <span className="text-text-tertiary">{t('settings.chainNodeDefaultPrimary')}:</span>{' '}
                        <span className="text-text-primary">{DEFAULT_NODE_URLS.primary}</span>
                      </div>
                      <div>
                        <span className="text-text-tertiary">{t('settings.chainNodeDefaultFallback')}:</span>{' '}
                        <span className="text-text-primary">{DEFAULT_NODE_URLS.fallback}</span>
                      </div>
                    </div>
                  </div>
                </InfoTooltip>
              )}
            </div>
            <SegmentedControl<ChainNodeSource>
              options={['gonka-hub', 'preset', 'custom'] as const}
              value={chainNodeSource}
              onChange={(v) => void onChainNodeSourceChange(v)}
              renderLabel={(v) =>
                v === 'gonka-hub'
                  ? t('settings.chainNodeGonkaHub')
                  : v === 'preset'
                    ? t('settings.chainNodePreset')
                  : t('settings.chainNodeCustom')
              }
              ariaLabel={t('settings.chainNode')}
            />
          </div>
          {chainNodeSource === 'preset' && (
            <div>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
                {t('settings.chainNodePickNode')}
              </div>
              <div className="flex flex-col gap-1.5">
                {PRESET_NODES.map((node) => {
                  const checked = presetNodeId === node.id;
                  return (
                    <label
                      key={node.id}
                      className={[
                        'flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 transition-colors',
                        checked ? 'bg-surface-2' : 'bg-transparent hover:bg-surface-2',
                      ].join(' ')}
                    >
                      <input
                        type="radio"
                        name="preset-node"
                        value={node.id}
                        checked={checked}
                        onChange={() => void onPresetNodeChange(node.id)}
                        className="h-3.5 w-3.5"
                      />
                      <span className="text-[13px] font-medium text-text-primary">{node.label}</span>
                      <span className="ml-auto font-mono text-[10px] text-text-tertiary">{node.url}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          {chainNodeSource === 'custom' && (
            <div>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
                {t('settings.customNodeUrl')}
              </div>
              <input
                value={customNodeDraft}
                onChange={(e) => setCustomNodeDraft(e.target.value)}
                onBlur={() => void onCustomNodeBlur()}
                placeholder="https://my-node.example.com"
                className="w-full rounded-lg bg-surface-2 px-3 py-2 text-sm font-mono text-text-primary outline-none"
              />
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* About */}
      <CollapsibleSection storageKey="about" icon={<InfoIcon />} label={t('settings.section.about')}>
        <ListCard>
          <Row
            label={t('settings.privacy')}
            value={<span className="text-lg text-text-disabled">›</span>}
            onClick={() => navigate('/settings/privacy')}
          />
          <div className="grid grid-cols-[1fr_auto] items-center gap-3 py-[11px]">
            <div className="text-[13px] font-medium text-text-primary">{t('settings.version')}</div>
            <div className="font-mono text-[12px] text-text-secondary">0.3.0-rc5</div>
          </div>
          <div className="grid grid-cols-[1fr_auto] items-center gap-3 py-[11px]">
            <div className="text-[13px] font-medium text-text-primary">{t('settings.commit')}</div>
            <div className="truncate font-mono text-[12px] text-text-secondary">{commit}</div>
          </div>
          <a
            href="https://github.com/gonkahub/wallet/blob/main/LICENSE"
            target="_blank"
            rel="noopener"
            className="grid grid-cols-[1fr_auto] items-center gap-3 py-[11px]"
          >
            <div className="text-[13px] font-medium text-text-primary">{t('settings.license')}</div>
            <span className="text-link text-[13px]">↗</span>
          </a>
        </ListCard>
      </CollapsibleSection>

      {/* Danger Zone */}
      <CollapsibleSection
        storageKey="danger"
        icon={<TrashIcon />}
        label={t('settings.dangerZone')}
        danger
        defaultOpen={false}
      >
        <ListCard className="border border-neg/30">
          <div className="py-[11px]">
            <input
              value={wipeConfirm}
              onChange={(e) => setWipeConfirm(e.target.value)}
              placeholder={t('settings.wipeConfirm')}
              className="mb-2 w-full rounded-lg bg-surface-2 px-3 py-2 text-sm outline-none"
            />
            <Button
              variant="danger"
              onClick={() => void wipe()}
              disabled={wipeConfirm !== 'WIPE'}
              fullWidth
            >
              {t('settings.wipe')}
            </Button>
          </div>
        </ListCard>
      </CollapsibleSection>

      <ConfirmDialog
        open={removeTarget !== null}
        destructive
        title={
          removeTarget?.type === 'wallet'
            ? t('settings.confirmRemoveWalletTitle')
            : t('settings.confirmRemoveWatchedTitle')
        }
        body={
          <>
            <div className="mb-2 font-mono text-[13px] text-text-primary">
              {removeTarget?.name}
            </div>
            {removeTarget?.type === 'wallet'
              ? t('settings.confirmRemoveWalletBody')
              : t('settings.confirmRemoveWatchedBody')}
          </>
        }
        confirmLabel={t('settings.confirmRemove')}
        cancelLabel={t('settings.cancel')}
        onConfirm={() => void confirmRemove()}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
}

/**
 * Tiny filled-triangle chevron. Points down when open, rotates -90° to
 * point right when collapsed. Matches the dot-matrix fill aesthetic of
 * the settings icons — no stroke, just solid currentColor shape.
 */
function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="currentColor"
      aria-hidden
      className={[
        'transition-transform duration-200 ease-out text-text-tertiary',
        open ? 'rotate-0' : '-rotate-90',
      ].join(' ')}
    >
      <path d="M1 3l4 4 4-4z" />
    </svg>
  );
}

/**
 * Click-to-collapse settings section. Persists open/closed per storageKey
 * in localStorage so the user's preference survives reloads. Animation via
 * framer-motion height:auto.
 */
function CollapsibleSection({
  storageKey,
  icon,
  label,
  danger = false,
  defaultOpen = true,
  children,
}: {
  readonly storageKey: string;
  readonly icon: ReactNode;
  readonly label: string;
  readonly danger?: boolean;
  readonly defaultOpen?: boolean;
  readonly children: ReactNode;
}) {
  const key = `settings.collapse.${storageKey}`;
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return defaultOpen;
    const saved = window.localStorage.getItem(key);
    if (saved === 'closed') return false;
    if (saved === 'open') return true;
    return defaultOpen;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, open ? 'open' : 'closed');
  }, [open, key]);

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="mb-3 flex w-full items-center gap-2 text-left"
      >
        <div
          className={[
            'flex h-7 w-7 items-center justify-center rounded-lg',
            danger ? 'bg-neg/12 text-neg' : 'bg-surface-2 text-text-primary',
          ].join(' ')}
        >
          {icon}
        </div>
        <span
          className={[
            'flex-1 text-sm font-semibold',
            danger ? 'text-neg' : 'text-text-primary',
          ].join(' ')}
        >
          {label}
        </span>
        <Chevron open={open} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
